import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Network, Truck, MapPin, Shield, Loader2, AlertTriangle, ChevronRight } from "lucide-react";
import { LRDirectAtlasCard, LRDirectAtlasCardHeader } from "./LRDirectAtlasCard";
import {
  useLRDirectBrokerMatch,
  SCORE_MIN_BIDS,
  LIMITED_MIN_BIDS,
} from "@/hooks/useLRDirectBrokerMatch";

/**
 * LR Direct ATLAS Broker Match — real data only.
 * Ranks the brokers in the shipper's own private network(s) from measured bid history.
 * A composite score is shown ONLY for brokers with >= SCORE_MIN_BIDS real bids.
 * Everything else shows raw counts and an honest "not enough history yet" state.
 */
export default function LRDirectBrokerMatchCard() {
  const navigate = useNavigate();
  const { rows, scored, loading, error, memberCount, totalBids, lanesCovered, hasRanking } =
    useLRDirectBrokerMatch(null);

  const top = useMemo(() => rows.slice(0, 3), [rows]);

  const topTargets = useMemo(
    () => (hasRanking ? scored : rows).slice(0, 5).filter((r) => r.bids > 0),
    [hasRanking, scored, rows],
  );

  const postToTop = () => {
    const ids = topTargets.map((r) => r.broker_user_id);
    const net = topTargets[0]?.network_ids?.[0];
    const qs = new URLSearchParams();
    if (net) qs.set("network", net);
    if (ids.length) qs.set("brokers", ids.join(","));
    navigate(`/lr-direct/post/start${qs.toString() ? `?${qs.toString()}` : ""}`);
  };

  const stats = [
    { label: "Network", value: loading ? "—" : String(memberCount), icon: Truck },
    { label: "Bids", value: loading ? "—" : String(totalBids), icon: MapPin },
    { label: "Ranked", value: loading ? "—" : String(scored.length), icon: Shield },
  ];

  const bannerText = loading
    ? "Reading your network's bid history"
    : memberCount === 0
    ? "No brokers in your private network yet"
    : hasRanking
    ? `${scored.length} of ${memberCount} broker${memberCount !== 1 ? "s" : ""} have enough history to rank`
    : `Ranking unlocks after a broker bids on ${SCORE_MIN_BIDS} of your loads`;

  const tone = hasRanking
    ? { bg: "hsl(210 90% 50% / 0.08)", fg: "hsl(210 70% 42%)" }
    : { bg: "hsl(var(--muted) / 0.6)", fg: "hsl(var(--muted-foreground))" };

  return (
    <LRDirectAtlasCard className="flex flex-col">
      <LRDirectAtlasCardHeader
        icon={<Users className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="ATLAS Broker Match"
      >
        <span
          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ backgroundColor: "hsl(170 50% 15% / 0.12)", color: "hsl(170 50% 15%)" }}
        >
          {hasRanking ? "Live" : "Beta"}
        </span>
      </LRDirectAtlasCardHeader>

      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">
        <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: tone.bg }}>
          <Network className="h-3 w-3 shrink-0" style={{ color: tone.fg }} />
          <span className="text-[11px] font-semibold" style={{ color: tone.fg }}>
            {bannerText}
          </span>
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          ATLAS ranks the brokers in your private network from their real LR Direct history — lanes bid, quote
          speed, and award rate. Numbers only, never estimates.
        </p>

        <div className="grid grid-cols-3 gap-1.5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-md px-2 py-1.5 bg-muted/50">
              <div className="flex items-center gap-1 mb-0.5">
                <s.icon className="h-2.5 w-2.5 text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground uppercase tracking-wide truncate">
                  {s.label}
                </span>
              </div>
              <span
                className="text-xs font-semibold"
                style={{
                  color: loading ? "hsl(var(--muted-foreground) / 0.7)" : "hsl(var(--brand-teal-deep))",
                }}
              >
                {s.value}
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-1.5">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">
            {hasRanking ? "Top matches" : "Your network brokers"}
          </span>

          {loading ? (
            <div className="flex items-center gap-2 py-3 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-[11px]">Reading bid history…</span>
            </div>
          ) : error ? (
            <div className="flex items-start gap-1.5 py-2">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-destructive" />
              <span className="text-[11px] text-destructive">{error}</span>
            </div>
          ) : memberCount === 0 ? (
            <p className="text-[11px] text-muted-foreground py-2">
              Add brokers to your private network first — ranking needs their bid history on your loads.
            </p>
          ) : (
            <>
              {top.map((r) => (
                <div key={r.broker_user_id} className="space-y-0.5">
                  <div className="flex items-center justify-between text-[11px] gap-2">
                    <span className="text-foreground font-medium truncate">{r.broker_name}</span>
                    {r.tier === "scored" ? (
                      <span className="font-semibold shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                        {r.score}
                        <span className="text-[9px] font-normal text-muted-foreground"> match</span>
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {r.bids >= LIMITED_MIN_BIDS ? "Limited history" : "Not enough history yet"}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {r.evidence}
                    {r.tier === "scored" && r.awardRate != null
                      ? ` · ${Math.round(r.awardRate * 100)}% award rate`
                      : ""}
                  </div>
                </div>
              ))}

              {!hasRanking && (
                <p className="text-[10px] text-muted-foreground pt-1">
                  No scores shown — a broker needs {SCORE_MIN_BIDS} bids on your loads before ATLAS ranks them.
                </p>
              )}
              {memberCount > top.length && (
                <p className="text-[10px] text-muted-foreground pt-0.5">
                  +{memberCount - top.length} more broker{memberCount - top.length !== 1 ? "s" : ""} in your network.
                </p>
              )}
            </>
          )}
        </div>

        <div className="mt-auto pt-1">
          <button
            onClick={postToTop}
            disabled={loading || topTargets.length === 0}
            className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors disabled:opacity-60"
            style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
          >
            {topTargets.length > 0
              ? `Post to Top ${topTargets.length} Broker${topTargets.length !== 1 ? "s" : ""}`
              : "Post to Top Brokers"}
            <ChevronRight className="h-3 w-3 ml-auto" />
          </button>
          {!loading && topTargets.length === 0 && memberCount > 0 && (
            <p className="text-[10px] text-muted-foreground pt-1">
              None of your network brokers has bid yet, so there is nothing to rank or pre-target.
            </p>
          )}
        </div>
      </div>
    </LRDirectAtlasCard>
  );
}
